/**
 * Units (`00_index.md` §5; `10_simulation_engine.md` SIM-006; `25_copy_and_tone.md` TON-033).
 *
 * Inside the code every value is SI: metres, seconds, degrees Celsius, metres per second,
 * hectopascals. Yards, feet, miles an hour and °F exist only here, at the edge where a
 * number is shown to a player. Golf distances show in yards by default and putts in feet,
 * switchable to metres (h18-24). A display helper returns a number and a unit code; the
 * words come from `@fairway/content` (TON-033: a template takes the unit as a parameter and
 * never holds a unit literal).
 *
 * Green speed is in Stimpmeter feet everywhere and is never converted (`06` CLK-113).
 */

/** Metres in one yard (exact). */
export const METRES_PER_YARD = 0.9144;
/** Metres in one foot (exact). */
export const METRES_PER_FOOT = 0.3048;
/** Metres in one inch (exact). */
export const METRES_PER_INCH = 0.0254;
/** Metres per second in one mile an hour (exact). */
export const MPS_PER_MPH = 0.44704;
/** Metres per second in one kilometre an hour. */
export const MPS_PER_KPH = 1 / 3.6;

export const yardsToMetres = (yd: number): number => yd * METRES_PER_YARD;
export const metresToYards = (m: number): number => m / METRES_PER_YARD;
export const feetToMetres = (ft: number): number => ft * METRES_PER_FOOT;
export const metresToFeet = (m: number): number => m / METRES_PER_FOOT;
export const inchesToMetres = (inches: number): number => inches * METRES_PER_INCH;
export const metresToInches = (m: number): number => m / METRES_PER_INCH;
export const mphToMps = (mph: number): number => mph * MPS_PER_MPH;
export const mpsToMph = (mps: number): number => mps / MPS_PER_MPH;
export const kphToMps = (kph: number): number => kph * MPS_PER_KPH;
export const mpsToKph = (mps: number): number => mps / MPS_PER_KPH;
export const celsiusToFahrenheit = (c: number): number => (c * 9) / 5 + 32;
export const fahrenheitToCelsius = (f: number): number => ((f - 32) * 5) / 9;

/** The player's choice for lengths (h18-24): yards and feet by default, or metres. */
export type LengthSystem = 'yards' | 'metres';
/** The player's choice for wind speed. */
export type SpeedUnit = 'mph' | 'kph' | 'mps';
/** The player's choice for temperature. */
export type TemperatureUnit = 'c' | 'f';

/** What a length is for: a golf distance (yards) or a putt and other short lengths (feet). */
export type LengthPurpose = 'golf' | 'putt';

/** A number to show and the code of its unit; `@fairway/content` turns the code into words. */
export interface DisplayValue<U extends string> {
  readonly value: number;
  readonly unit: U;
}

/** Rounds half away from zero to `dp` decimal places. */
export function roundTo(x: number, dp: number): number {
  const f = 10 ** dp;
  const r = Math.round(Math.abs(x) * f) / f;
  return x < 0 ? -r : r;
}

/**
 * A length in metres, as the player sees it. Golf distances: whole yards or whole metres.
 * Putts: feet to one decimal place under 10 ft and whole feet from there, or metres to one
 * decimal place.
 */
export function displayLength(
  metres: number,
  system: LengthSystem = 'yards',
  purpose: LengthPurpose = 'golf',
): DisplayValue<'yd' | 'ft' | 'm'> {
  if (system === 'metres') return { value: roundTo(metres, purpose === 'putt' ? 1 : 0), unit: 'm' };
  if (purpose === 'golf') return { value: roundTo(metresToYards(metres), 0), unit: 'yd' };
  const ft = metresToFeet(metres);
  return { value: roundTo(ft, Math.abs(ft) < 10 ? 1 : 0), unit: 'ft' };
}

/** A speed in metres per second, as the player sees it, to whole units. */
export function displaySpeed(mps: number, unit: SpeedUnit = 'mph'): DisplayValue<SpeedUnit> {
  const v = unit === 'mph' ? mpsToMph(mps) : unit === 'kph' ? mpsToKph(mps) : mps;
  return { value: roundTo(v, 0), unit };
}

/** A temperature in °C, as the player sees it, to whole degrees. */
export function displayTemperature(
  celsius: number,
  unit: TemperatureUnit = 'c',
): DisplayValue<TemperatureUnit> {
  return { value: roundTo(unit === 'f' ? celsiusToFahrenheit(celsius) : celsius, 0), unit };
}

/** A length typed by the player, back to metres (the one way back in from the display layer). */
export function parseLength(value: number, unit: 'yd' | 'ft' | 'm'): number {
  return unit === 'yd' ? yardsToMetres(value) : unit === 'ft' ? feetToMetres(value) : value;
}
